/**
 * User presence management utilities
 */

export const TYPING_TIMEOUT_MS = 5000;
export const AUTO_AWAY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export type PresenceState = "active" | "away" | "offline";
export type PresenceMode = "away" | "auto";

interface UserPresenceData {
  is_online: boolean;
  last_active_at?: number | null;
}

/**
 * How long after last_active_at before a non-online user transitions from
 * "away" to "offline".  Set to 3x the auto-away threshold so the yellow
 * dot persists briefly after the heartbeat marks is_online = false.
 */
const OFFLINE_GRACE_MS = AUTO_AWAY_TIMEOUT_MS * 3;

/**
 * Get the current presence state for a user.
 * - "active": online and recently active
 * - "away": online but idle for > AUTO_AWAY_TIMEOUT_MS, or recently went
 *   offline (within OFFLINE_GRACE_MS of last activity)
 * - "offline": not online and no recent activity
 */
export function getPresenceState(user: UserPresenceData): PresenceState {
  if (!user.is_online) {
    // If the user was recently active, show "away" instead of "offline".
    // This covers the auto-away case where is_online is set to false but
    // the user was active not long ago.
    if (user.last_active_at != null) {
      const elapsed = Date.now() - user.last_active_at;
      if (elapsed < OFFLINE_GRACE_MS) {
        return "away";
      }
    }
    return "offline";
  }

  if (user.last_active_at != null) {
    const now = Date.now();
    if (now - user.last_active_at > AUTO_AWAY_TIMEOUT_MS) {
      return "away";
    }
  }

  return "active";
}

/**
 * Returns a Tailwind class for the presence dot color.
 */
export function presenceDotClass(state: PresenceState): string {
  switch (state) {
    case "active":
      return "bg-success";
    case "away":
      return "bg-warning";
    case "offline":
      return "bg-muted-foreground/30";
  }
}

interface SetPresenceResult {
  is_online?: boolean;
  manual_status: "away" | null;
}

/**
 * Set the presence state for a user
 * Only allows setting to "away" or "auto" - cannot force to "active"
 */
export function setPresenceState(mode: PresenceMode): SetPresenceResult {
  if (mode !== "away" && mode !== "auto") {
    throw new Error('Presence mode must be "away" or "auto"');
  }

  if (mode === "away") {
    return {
      is_online: false,
      manual_status: "away",
    };
  }

  // "auto" mode - clear manual status
  return {
    manual_status: null,
  };
}

interface AutoAwayOptions {
  isBot?: boolean;
  manualStatus?: string | null;
}

/**
 * Check if a user should be automatically set to away
 */
export function shouldAutoAway(
  lastActivityAt: number,
  options: AutoAwayOptions = {},
): boolean {
  // Bot users are exempt from auto-away
  if (options.isBot) {
    return false;
  }

  // If manual status is set, don't auto-away
  if (options.manualStatus) {
    return false;
  }

  const now = Date.now();
  return now - lastActivityAt > AUTO_AWAY_TIMEOUT_MS;
}

/**
 * Clear the typing indicator for a user
 */
export function clearTypingIndicator(): {
  typing_in: null;
  typing_at: null;
} {
  return {
    typing_in: null,
    typing_at: null,
  };
}
